import { RightOutlineIcon } from '@carboncopyco/icons';
import { TourFromScratchIllustration } from '@carboncopyco/illustrations';
import {
  Button,
  InputText,
  Modal,
  TextBase,
  TextLargeParagraph,
  TextSectionHeader,
} from '@carboncopyco/ui';
import { convertToProtocolUrl } from '@carboncopyco/utils';
import { ErrorMessage, Field, type FieldProps, Form, Formik } from 'formik';

interface CreateTourFromScratchProps {
  onClose: () => void;
  open: boolean;
  onSubmit: (url: string) => void;
  title?: string;
}
type ErrorsObject = Record<'url', string>;

export const CreateTourFromScratch = ({
  onClose,
  open,
  onSubmit,
  title = 'Choose a base for your tour',
}: CreateTourFromScratchProps) => {
  const isURL = (str: string) => {
    return /^(?:\w+:)?\/\/([^\s.]+\.\S{2}|localhost[:?\d]*)\S*$/.test(str);
  };

  const modalTitle = <div className="flex items-center">{title}</div>;

  return (
    <Modal
      onClose={onClose}
      open={open}
      title={modalTitle}
    >
      <div className="w-ds-lg-modal h-ds-440 flex flex-col pt-14 ">
        <div className="flex flex-col items-center mt-ds-5 flex-grow">
          <TourFromScratchIllustration />
          <TextSectionHeader
            color="text-ds-natural-600"
            label="Capture tour from a live app"
          />
          <TextLargeParagraph
            label="Type your app's URL to capture the tour frames"
            color="text-ds-natural-300"
          />
          <Formik
            initialValues={{ url: '' }}
            validateOnChange={true}
            validateOnBlur={true}
            onSubmit={(values, actions) => {
              actions.setFieldValue('url', convertToProtocolUrl(values.url));

              return onSubmit(convertToProtocolUrl(values.url));
            }}
            validate={(values) => {
              const errors = {} as ErrorsObject;
              if (!values.url) {
                errors.url = 'URL cannot be empty.';
                return errors;
              }
              if (!isURL(convertToProtocolUrl(values.url))) {
                errors.url =
                  'The URL you typed does not seem to be in the correct structure. Please try again.';
                return errors;
              }
              return errors;
            }}
          >
            {({ handleSubmit, errors, isSubmitting }) => {
              return (
                <Form
                  onSubmit={handleSubmit}
                  className="flex flex-col flex-grow"
                >
                  <div className="flex flex-col items-center">
                    <div className="w-ds-500 h-10 mt-ds-5">
                      <Field
                        name="url"
                        placeholder="Type URL here"
                        autoFocus
                        className="w-ds-500"
                        autoComplete="off"
                      >
                        {({ field, form, ...props }: FieldProps) => {
                          return (
                            <InputText
                              placeholder="Enter URL"
                              autoFocus
                              className="h-10"
                              {...field}
                              {...props}
                            />
                          );
                        }}
                      </Field>
                    </div>
                    <ErrorMessage name="url">
                      {(msg) => (
                        <div className="text-left pl-3">
                          <TextBase
                            label={msg}
                            color="text-ds-danger-300"
                          />
                        </div>
                      )}
                    </ErrorMessage>
                  </div>
                  <div className="flex justify-end items-center w-ds-lg-modal mt-auto">
                    <Button
                      label="Back"
                      link
                      dark
                      onClick={onClose}
                    />
                    <Button
                      label="Let's Go!"
                      rightIcon={<RightOutlineIcon />}
                      disabled={!!errors?.url || isSubmitting}
                      submit
                    />
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
      </div>
    </Modal>
  );
};
